/**
 * The app shell: board, keypad, controls, hint ladder, settings.
 *
 * One class owns the state and re-renders on change. There is no framework by
 * design -- the whole interactive surface is 81 cells plus a keypad, and a
 * runtime would cost more bytes than the engine it wraps on a PWA that has to
 * boot offline.
 */

import { CELLS, EMPTY, SIZE, boardFromString, cellName, colOf, rowOf, type Digit } from "../core/grid";
import { nextStep } from "../core/solver-human";
import { TECHNIQUE_BY_ID, TIER_ORDER } from "../core/techniques";
import type { Step, Tier } from "../core/techniques/types";
import { Game, formatDuration } from "../state/game";
import { DEFAULT_SETTINGS, loadSettings, saveSettings, type Settings } from "../state/settings";
import {
  clearGame,
  loadGame,
  loadStats,
  medianMs,
  recordSolved,
  recordStarted,
  saveGame,
} from "../state/storage";
import { requestPuzzle } from "../workers/puzzle-client";
import { BoardView } from "./board";
import { icons } from "./icons";

const TIER_LABEL: Record<Tier | "unrated", string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  expert: "Expert",
  evil: "Evil",
  unrated: "Unrated",
};

/**
 * The hint ladder. Each rung says strictly more than the last, so a player can
 * take the smallest push that unsticks them instead of being handed the answer.
 */
const HINT_RUNGS = ["nudge", "name", "cells", "move"] as const;
type HintRung = (typeof HINT_RUNGS)[number];

interface HintState {
  step: Step;
  rung: HintRung;
}

/** The settings that are simple on/off toggles. */
type BooleanSetting = {
  [K in keyof Settings]: Settings[K] extends boolean ? K : never;
}[keyof Settings];

export class App {
  readonly element = document.createElement("div");

  private game: Game | null = null;
  private settings: Settings = { ...DEFAULT_SETTINGS };
  private selected: number | null = null;
  private lockedDigit: Digit | null = null;
  private notesMode = false;
  private hint: HintState | null = null;
  private busy = false;
  private saveTimer: number | null = null;
  private clockTimer: number | null = null;

  private readonly board: BoardView;
  private readonly keys: HTMLButtonElement[] = [];
  private readonly nodes: {
    tier: HTMLElement;
    requires: HTMLDetailsElement;
    timer: HTMLElement;
    pause: HTMLButtonElement;
    keypad: HTMLElement;
    notes: HTMLButtonElement;
    undo: HTMLButtonElement;
    redo: HTMLButtonElement;
    hintBar: HTMLElement;
    status: HTMLElement;
    overlay: HTMLElement;
  };

  constructor() {
    this.settings = loadSettings();
    this.board = new BoardView((cell) => this.selectCell(cell));
    this.element.className = "app";
    this.nodes = this.buildChrome();
    this.applyTheme();
    this.bindKeyboard();
  }

  // ---------- construction ----------

  private buildChrome() {
    const tier = el("span", "badge");
    const requires = document.createElement("details");
    requires.className = "requires";

    const timer = el("span", "timer");
    const pause = iconButton("pause", "Pause", () => this.togglePause());

    const topbar = el("header", "topbar");
    topbar.append(el("div", "meta", [tier, requires]), el("div", "spacer"), timer, pause);

    const keypad = el("div", "keypad");
    for (let digit = 1 as Digit; digit <= 9; digit = (digit + 1) as Digit) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "key";
      button.append(el("span", "key-digit", [text(String(digit))]));
      button.append(el("span", "key-count"));
      button.addEventListener("click", () => this.pressDigit(digit));
      keypad.append(button);
      this.keys.push(button);
    }

    const notes = iconButton("notes", "Notes", () => this.toggleNotes());
    const undo = iconButton("undo", "Undo", () => this.mutate(() => this.game?.undo() ?? false));
    const redo = iconButton("redo", "Redo", () => this.mutate(() => this.game?.redo() ?? false));
    const erase = iconButton("erase", "Erase", () => this.eraseSelected());
    const hintBtn = iconButton("hint", "Hint", () => this.advanceHint());

    const controls = el("div", "controls", [undo, redo, erase, notes, hintBtn]);
    const hintBar = el("div", "hintbar");
    hintBar.hidden = true;

    const side = el("div", "side", [keypad, controls]);
    const main = el("main", "play", [el("div", "board-wrap", [this.board.element]), side]);

    const newGame = textButton("newGame", "New game", () => this.openNewGame());
    const settingsBtn = textButton("settings", "Settings", () => this.openSettings());
    const footer = el("footer", "footer", [newGame, settingsBtn]);

    // Polite so it does not interrupt a screen reader mid-cell; the grid's own
    // labels carry per-cell detail, this is for events like "Solved".
    const status = el("div", "sr-only");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");

    const overlay = el("div", "overlay");
    overlay.hidden = true;

    this.element.append(topbar, main, hintBar, footer, status, overlay);
    return { tier, requires, timer, pause, keypad, notes, undo, redo, hintBar, status, overlay };
  }

  /**
   * Update the keys in place. Re-creating them would detach the node the
   * player is tabbed onto, dropping keyboard focus on every keystroke.
   */
  private updateKeypad(): void {
    const game = this.game;
    this.keys.forEach((button, index) => {
      const digit = (index + 1) as Digit;
      const remaining = game ? game.remaining(digit) : 9;
      const locked = this.lockedDigit === digit;
      button.classList.toggle("is-done", remaining === 0);
      button.classList.toggle("is-locked", locked);
      button.setAttribute(
        "aria-label",
        `${digit}, ${remaining} remaining${locked ? ", selected" : ""}`,
      );
      button.setAttribute("aria-pressed", String(locked));
      button.querySelector(".key-count")!.textContent = remaining === 0 ? "" : String(remaining);
    });
  }

  // ---------- lifecycle ----------

  async boot(): Promise<void> {
    const saved = await loadGame();
    if (saved) {
      this.game = Game.restore(saved);
      this.selected = firstEmptyCell(this.game);
      this.startClock();
      this.render();
      return;
    }
    await this.newGame("easy");
  }

  private async newGame(tier: Tier): Promise<void> {
    this.busy = true;
    this.setStatus(`Generating a ${TIER_LABEL[tier]} puzzle...`);
    this.render();

    try {
      const payload = await requestPuzzle(tier);
      this.game = Game.create(
        boardFromString(payload.puzzle),
        boardFromString(payload.solution),
        { tier: payload.tier, techniques: payload.techniques as never, seed: payload.seed },
      );
      this.selected = firstEmptyCell(this.game);
      this.lockedDigit = null;
      this.hint = null;
      void recordStarted(payload.tier);
      this.startClock();
      this.setStatus(`New ${TIER_LABEL[payload.tier]} puzzle, ${payload.clues} clues.`);
    } catch (error) {
      this.setStatus((error as Error).message);
    } finally {
      this.busy = false;
      this.render();
      void this.save();
    }
  }

  private startClock(): void {
    this.game?.start();
    if (this.clockTimer !== null) return;
    this.clockTimer = window.setInterval(() => {
      if (!this.game) return;
      this.nodes.timer.textContent = formatDuration(this.game.elapsedMs());
    }, 500);
  }

  private async save(): Promise<void> {
    if (!this.game) return;
    const snapshot = this.game.toSnapshot();
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    // Debounced: a fast typist should not queue an IndexedDB write per keypress.
    this.saveTimer = window.setTimeout(() => {
      void saveGame(snapshot);
    }, 250);
  }

  // ---------- input ----------

  private selectCell(cell: number): void {
    if (!this.game) return;

    if (this.settings.inputMode === "digit-first" && this.lockedDigit !== null) {
      this.selected = cell;
      this.place(cell, this.lockedDigit);
      return;
    }
    this.selected = cell;
    this.render();
    this.board.focusSelected(cell);
  }

  private pressDigit(digit: Digit): void {
    if (!this.game) return;

    if (this.settings.inputMode === "digit-first") {
      this.lockedDigit = this.lockedDigit === digit ? null : digit;
      this.render();
      return;
    }
    if (this.selected === null) return;
    this.place(this.selected, digit);
  }

  private place(cell: number, digit: Digit, markKindOverride?: "corner" | "center"): void {
    const game = this.game;
    if (!game || game.isGiven(cell)) return;

    this.mutate(() => {
      if (markKindOverride) return game.toggleMark(cell, digit, markKindOverride);
      if (this.notesMode) return game.toggleMark(cell, digit, this.settings.markKind);
      // Pressing the digit already there clears it -- the standard way to undo
      // a placement without reaching for the eraser.
      return game.setValue(cell, game.valueAt(cell) === digit ? 0 : digit, {
        cleanPeerMarks: this.settings.cleanPeerMarks,
      });
    });
  }

  private eraseSelected(): void {
    if (this.selected === null) return;
    this.mutate(() => this.game?.erase(this.selected!) ?? false);
  }

  private toggleNotes(): void {
    this.notesMode = !this.notesMode;
    this.render();
  }

  /** Run a state change, then re-render, persist and check for a win. */
  private mutate(change: () => boolean): void {
    if (!this.game) return;
    const changed = change();
    if (!changed) return;
    this.hint = null;
    this.render();
    void this.save();
    this.checkWin();
  }

  private moveSelection(dRow: number, dCol: number): void {
    const from = this.selected ?? 0;
    const row = Math.min(SIZE - 1, Math.max(0, rowOf(from) + dRow));
    const col = Math.min(SIZE - 1, Math.max(0, colOf(from) + dCol));
    this.selected = row * SIZE + col;
    this.render();
    this.board.focusSelected(this.selected);
  }

  private bindKeyboard(): void {
    this.element.addEventListener("keydown", (event) => {
      if (!this.game || this.busy) return;
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "SELECT") return;

      const key = event.key;
      const ctrl = event.ctrlKey || event.metaKey;

      if (ctrl && key.toLowerCase() === "z") {
        event.preventDefault();
        this.mutate(() => (event.shiftKey ? this.game!.redo() : this.game!.undo()));
        return;
      }
      if (ctrl && key.toLowerCase() === "y") {
        event.preventDefault();
        this.mutate(() => this.game!.redo());
        return;
      }

      switch (key) {
        case "ArrowUp":
          event.preventDefault();
          return this.moveSelection(-1, 0);
        case "ArrowDown":
          event.preventDefault();
          return this.moveSelection(1, 0);
        case "ArrowLeft":
          event.preventDefault();
          return this.moveSelection(0, -1);
        case "ArrowRight":
          event.preventDefault();
          return this.moveSelection(0, 1);
        case "Backspace":
        case "Delete":
        case "0":
          event.preventDefault();
          return this.eraseSelected();
        case "n":
        case "N":
          event.preventDefault();
          return this.toggleNotes();
        case "h":
        case "H":
          event.preventDefault();
          return this.advanceHint();
        case "p":
        case "P":
          event.preventDefault();
          return this.togglePause();
        default:
          break;
      }

      if (key >= "1" && key <= "9") {
        event.preventDefault();
        const digit = Number(key) as Digit;
        if (this.selected === null) return;
        // Shift always writes the note row you are not currently using, so both
        // corner and centre marks are reachable without changing mode.
        if (event.shiftKey) {
          const other = this.settings.markKind === "corner" ? "center" : "corner";
          this.place(this.selected, digit, this.notesMode ? other : this.settings.markKind);
        } else {
          this.place(this.selected, digit);
        }
      }
    });
  }

  // ---------- hints ----------

  private advanceHint(): void {
    const game = this.game;
    if (!game) return;

    if (!this.hint) {
      const step = nextStep(game.board());
      if (!step) {
        this.setStatus(
          game.isComplete()
            ? "The grid is full."
            : "No further move is available from the digits placed -- check for a mistake.",
        );
        return;
      }
      this.hint = { step, rung: "nudge" };
    } else {
      const index = HINT_RUNGS.indexOf(this.hint.rung);
      if (index < HINT_RUNGS.length - 1) this.hint.rung = HINT_RUNGS[index + 1]!;
    }
    this.render();
  }

  private applyHint(): void {
    const game = this.game;
    const hint = this.hint;
    if (!game || !hint) return;

    this.mutate(() => {
      let changed = false;
      for (const { cell, digit } of hint.step.placements) {
        changed = game.setValue(cell, digit, { cleanPeerMarks: this.settings.cleanPeerMarks }) || changed;
      }
      return changed;
    });
    // An elimination-only step has nothing to place; clearing the hint and
    // saying so beats a button that silently does nothing.
    if (hint.step.placements.length === 0) {
      this.hint = null;
      this.setStatus("That step removes candidates rather than placing a digit.");
      this.render();
    }
  }

  private renderHint(): void {
    const bar = this.nodes.hintBar;
    const hint = this.hint;
    if (!hint) {
      bar.hidden = true;
      bar.replaceChildren();
      return;
    }

    const technique = TECHNIQUE_BY_ID.get(hint.step.technique)!;
    const where = hint.step.pattern.length > 0 ? cellName(hint.step.pattern[0]!) : "";

    const lines: Record<HintRung, string> = {
      nudge: `There is a move available${where ? ` around ${where}` : ""}. Look again before taking more.`,
      name: `Try a ${technique.name}.`,
      cells: `${technique.name} -- the cells it uses are highlighted.`,
      move: hint.step.explanation,
    };

    bar.hidden = false;
    bar.replaceChildren();
    bar.append(el("p", "hint-text", [text(lines[hint.rung])]));

    const actions = el("div", "hint-actions");
    if (hint.rung !== "move") {
      actions.append(plainButton("Show me more", () => this.advanceHint()));
    } else if (hint.step.placements.length > 0) {
      actions.append(plainButton("Place it", () => this.applyHint()));
    }
    actions.append(
      plainButton("Dismiss", () => {
        this.hint = null;
        this.render();
      }),
    );
    bar.append(actions);
  }

  // ---------- overlays ----------

  private togglePause(): void {
    const game = this.game;
    if (!game) return;
    if (game.isRunning) {
      game.pause();
      this.showOverlay("Paused", [plainButton("Resume", () => this.togglePause())], { blur: true });
    } else {
      game.start();
      this.hideOverlay();
    }
    this.render();
  }

  private openNewGame(): void {
    const buttons = TIER_ORDER.map((tier) =>
      plainButton(TIER_LABEL[tier], () => {
        this.hideOverlay();
        void this.newGame(tier);
      }),
    );
    this.showOverlay("New game", [
      el("p", "overlay-note", [text("Difficulty is the hardest technique the puzzle needs.")]),
      el("div", "tier-choices", buttons),
      plainButton("Cancel", () => this.hideOverlay()),
    ]);
  }

  private async openSettings(): Promise<void> {
    const rows: HTMLElement[] = [];

    const toggle = (label: string, key: BooleanSetting, note?: string) => {
      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked = this.settings[key];
      input.addEventListener("change", () => {
        this.settings[key] = input.checked;
        saveSettings(this.settings);
        this.render();
      });
      const row = el("label", "setting", [input, el("span", "", [text(label)])]);
      if (note) row.append(el("small", "setting-note", [text(note)]));
      rows.push(row);
    };

    const choice = <K extends keyof Settings>(
      label: string,
      key: K,
      options: [Settings[K] & string, string][],
    ) => {
      const select = document.createElement("select");
      for (const [value, name] of options) {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = name;
        option.selected = this.settings[key] === value;
        select.append(option);
      }
      select.addEventListener("change", () => {
        // Object.assign sidesteps the generic-index write TS cannot narrow; the
        // option values are constrained to Settings[K] above, so it is sound.
        Object.assign(this.settings, { [key]: select.value });
        saveSettings(this.settings);
        this.applyTheme();
        this.render();
      });
      rows.push(el("label", "setting", [el("span", "", [text(label)]), select]));
    };

    choice("Input", "inputMode", [
      ["cell-first", "Cell first"],
      ["digit-first", "Digit first"],
    ]);
    choice("Notes go to", "markKind", [
      ["center", "Centre"],
      ["corner", "Corner (Snyder)"],
    ]);
    choice("Theme", "theme", [
      ["system", "Match system"],
      ["light", "Light"],
      ["dark", "Dark"],
    ]);
    toggle("Show all candidates", "autoCandidates", "Fills every empty cell with its true candidates.");
    toggle("Clear notes when placing", "cleanPeerMarks");
    toggle("Check my mistakes", "checkMistakes", "Off by default -- a purist run can go wrong in peace.");
    toggle("Highlight row, column and box", "highlightPeers");
    toggle("Highlight the same digit", "highlightSameDigit");
    toggle("Show the timer", "showTimer");

    const stats = await loadStats();
    const statRows = TIER_ORDER.filter((tier) => stats[tier]?.played).map((tier) => {
      const entry = stats[tier]!;
      const best = entry.bestMs === null ? "-" : formatDuration(entry.bestMs);
      const median = medianMs(entry.timesMs);
      return el("tr", "", [
        el("th", "", [text(TIER_LABEL[tier])]),
        el("td", "", [text(`${entry.solved}/${entry.played}`)]),
        el("td", "", [text(best)]),
        el("td", "", [text(median === null ? "-" : formatDuration(median))]),
      ]);
    });

    const body: HTMLElement[] = [el("div", "settings-list", rows)];
    if (statRows.length > 0) {
      const head = el("tr", "", [
        el("th", "", [text("")]),
        el("td", "", [text("Solved")]),
        el("td", "", [text("Best")]),
        el("td", "", [text("Median")]),
      ]);
      body.push(el("h3", "", [text("Statistics")]));
      body.push(el("table", "stats", [head, ...statRows]));
    }
    body.push(
      plainButton("Start over", () => {
        void clearGame();
        this.hideOverlay();
        void this.newGame("easy");
      }),
    );
    body.push(plainButton("Close", () => this.hideOverlay()));

    this.showOverlay("Settings", body);
  }

  private showOverlay(title: string, body: HTMLElement[], options: { blur?: boolean } = {}): void {
    const panel = el("div", "overlay-panel", [el("h2", "", [text(title)]), ...body]);
    this.nodes.overlay.replaceChildren(panel);
    this.nodes.overlay.hidden = false;
    this.nodes.overlay.classList.toggle("is-blurring", Boolean(options.blur));
    panel.querySelector("button")?.focus();
  }

  private hideOverlay(): void {
    this.nodes.overlay.hidden = true;
    this.nodes.overlay.replaceChildren();
  }

  private checkWin(): void {
    const game = this.game;
    if (!game || !game.isSolved()) return;
    game.pause();
    const ms = game.elapsedMs();
    void recordSolved(game.meta.tier, ms);
    void clearGame();
    this.setStatus(`Solved in ${formatDuration(ms)}.`);
    this.showOverlay("Solved", [
      el("p", "overlay-note", [
        text(`${TIER_LABEL[game.meta.tier]} in ${formatDuration(ms)}, with no ads and no interruptions.`),
      ]),
      plainButton("Another one", () => {
        this.hideOverlay();
        void this.newGame(game.meta.tier === "unrated" ? "medium" : game.meta.tier);
      }),
      plainButton("Close", () => this.hideOverlay()),
    ]);
  }

  // ---------- rendering ----------

  private applyTheme(): void {
    const root = document.documentElement;
    if (this.settings.theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", this.settings.theme);
  }

  private setStatus(message: string): void {
    this.nodes.status.textContent = message;
  }

  render(): void {
    const game = this.game;
    this.element.classList.toggle("is-busy", this.busy);
    if (!game) return;

    this.nodes.tier.textContent = TIER_LABEL[game.meta.tier];
    this.nodes.tier.dataset.tier = game.meta.tier;

    // Transparent difficulty: the techniques are the evidence behind the label,
    // so they are one click away rather than hidden.
    this.nodes.requires.replaceChildren();
    if (game.meta.techniques.length > 0) {
      const summary = document.createElement("summary");
      summary.textContent = "What it needs";
      const names = game.meta.techniques.map((id) => TECHNIQUE_BY_ID.get(id)?.name ?? id);
      this.nodes.requires.append(summary, el("p", "requires-list", [text(names.join(" - "))]));
    }

    this.nodes.timer.hidden = !this.settings.showTimer;
    this.nodes.timer.textContent = formatDuration(game.elapsedMs());
    setIcon(this.nodes.pause, game.isRunning ? "pause" : "play");
    this.nodes.pause.setAttribute("aria-label", game.isRunning ? "Pause" : "Resume");

    this.nodes.notes.classList.toggle("is-active", this.notesMode);
    this.nodes.notes.setAttribute("aria-pressed", String(this.notesMode));
    this.nodes.notes.setAttribute(
      "aria-label",
      `Notes (${this.settings.markKind === "corner" ? "corner" : "centre"})`,
    );
    this.nodes.undo.disabled = !game.canUndo;
    this.nodes.redo.disabled = !game.canRedo;

    this.updateKeypad();
    this.board.render(
      game,
      this.settings,
      { cell: this.selected, digit: this.lockedDigit },
      this.hint && this.hint.rung !== "nudge" && this.hint.rung !== "name"
        ? {
            pattern: new Set(this.hint.step.pattern),
            focus: new Set([
              ...this.hint.step.placements.map((p) => p.cell),
              ...this.hint.step.eliminations.map((e) => e.cell),
            ]),
          }
        : {},
    );
    this.renderHint();
  }
}

// ---------- small DOM helpers ----------

function text(value: string): Text {
  return document.createTextNode(value);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className = "",
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const child of children) node.append(child);
  return node;
}

function setIcon(button: HTMLButtonElement, name: keyof typeof icons): void {
  button.innerHTML = icons[name];
}

function iconButton(
  name: keyof typeof icons,
  label: string,
  onClick: () => void,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "icon-button";
  button.setAttribute("aria-label", label);
  button.title = label;
  button.innerHTML = icons[name];
  button.addEventListener("click", onClick);
  return button;
}

function textButton(
  name: keyof typeof icons,
  label: string,
  onClick: () => void,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "text-button";
  button.innerHTML = icons[name];
  button.append(text(label));
  button.addEventListener("click", onClick);
  return button;
}

function plainButton(label: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "plain-button";
  button.textContent = label;
  button.addEventListener("click", onClick);
  return button;
}

function firstEmptyCell(game: Game): number | null {
  for (let cell = 0; cell < CELLS; cell++) if (game.valueAt(cell) === EMPTY) return cell;
  return null;
}
